import type React from 'react';

/**
 * Whether `inserted` typed/pasted into `current` at [start, end) still leaves a plain number:
 * digits, and for decimal fields at most one `.` or `,`. Used to refuse keystrokes instead of
 * showing a validation error afterwards.
 */
export const isAllowedNumericInput = (
  current: string,
  start: number,
  end: number,
  inserted: string,
  options: { decimals: boolean }
): boolean => {
  const next = current.slice(0, start) + inserted + current.slice(end);
  return options.decimals ? /^\d*[.,]?\d*$/.test(next) : /^\d*$/.test(next);
};

type NumericGuardTarget = HTMLInputElement & { selectionStart: number | null };

/**
 * React handlers that keep a text input numeric at the source. `inputMode="decimal"` already
 * shows the numeric keyboard on phones, but the keyboard (and any paste) can still produce
 * letters; these refuse them before Formik sees the value.
 */
export const numericInputGuards = (options: { decimals: boolean }) => ({
  onBeforeInput: (event: React.FormEvent<HTMLInputElement>) => {
    const native = event.nativeEvent as InputEvent;
    const data = native.data;
    if (data == null) return; // deletions, IME composition and the like
    const target = event.currentTarget as NumericGuardTarget;
    const start = target.selectionStart ?? target.value.length;
    const end = target.selectionEnd ?? start;
    if (!isAllowedNumericInput(target.value, start, end, data, options)) {
      event.preventDefault();
    }
  },
  onPaste: (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text').trim();
    const target = event.currentTarget as NumericGuardTarget;
    const start = target.selectionStart ?? target.value.length;
    const end = target.selectionEnd ?? start;
    if (!isAllowedNumericInput(target.value, start, end, pasted, options)) {
      event.preventDefault();
    }
  },
});
